import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CreditBalance {
  workspaceId: string;
  balanceCredits: number;
  totalCredited: number;
  totalDebited: number;
  lastTransactionAt?: string | null;
}

export interface CreditPackage {
  id: number;
  key: string;
  name: string;
  description: string;
  priceCents: number;
  discountedPriceCents?: number | null;
  vatRate: number;
  priceWithVatCents: number;
  discountedPriceWithVatCents?: number | null;
  currency: string;
  baseCredits: number;
  bonusCredits: number;
  totalCredits: number;
  stripePriceId?: string | null;
  active: boolean;
  sortOrder: number;
}

export interface CreditTransaction {
  id: number;
  workspaceId: string;
  type: string;
  status: string;
  source?: string | null;
  amountCredits: number;
  amountCurrencyCents?: number | null;
  currency?: string | null;
  packageId?: number | null;
  usageId?: number | null;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface AdminCreditTransaction extends CreditTransaction {
  workspaceName: string;
  workspaceUsername?: string | null;
  workspaceEmail?: string | null;
  workspaceAccountType?: string | null;
  companyName?: string | null;
  packageKey?: string | null;
  packageName?: string | null;
  note?: string;
}

export interface CreditPricingRule {
  id: number;
  operationKey: string;
  label: string;
  category?: string | null;
  active: boolean;
  minCredits: number;
  reserveCredits: number;
  markupMultiplier: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedUnits: number;
  model?: string | null;
  provider?: string | null;
  modelGem?: string | null;
  modelOpenAI?: string | null;
  apiKey?: string | null;
}

export interface CreditEstimate {
  operationKey: string;
  category?: string | null;
  model?: string | null;
  providerCostUsd: number;
  estimatedCredits: number;
  reserveCredits: number;
  ruleSnapshot: CreditPricingRule;
}

export interface CreditTransactionFilters {
  search?: string;
  workspaceId?: string;
  type?: string;
  source?: string;
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CreditsService {
  private readonly baseUrl = `${environment.apiUrl}/credits`;
  private readonly adminBaseUrl = `${environment.apiUrl}/admin/credits`;
  private readonly walletRefreshSubject = new Subject<void>();
  readonly walletRefresh$ = this.walletRefreshSubject.asObservable();

  constructor(private http: HttpClient) {}

  notifyWalletRefresh(): void {
    this.walletRefreshSubject.next();
  }

  getBalance(): Observable<CreditBalance> {
    return this.http.get<CreditBalance>(`${this.baseUrl}/balance`);
  }

  getLedger(limit = 50): Observable<CreditTransaction[]> {
    return this.http.get<CreditTransaction[]>(`${this.baseUrl}/ledger`, { params: { limit: String(limit) } });
  }

  getPackages(): Observable<CreditPackage[]> {
    return this.http.get<CreditPackage[]>(`${this.baseUrl}/packages`);
  }

  createCheckoutSession(packageId: number, successUrl: string, cancelUrl: string): Observable<{ id: string; url: string }> {
    return this.http.post<{ id: string; url: string }>(`${this.baseUrl}/checkout-session`, {
      packageId,
      successUrl,
      cancelUrl
    });
  }

  estimate(operationKey: string, payload?: Partial<Pick<CreditEstimate, 'model'>> & { inputTokens?: number; outputTokens?: number; unitCount?: number }): Observable<CreditEstimate> {
    return this.http.post<CreditEstimate>(`${this.baseUrl}/estimate`, {
      operationKey,
      ...(payload || {})
    });
  }

  getAdminPackages(): Observable<CreditPackage[]> {
    return this.http.get<CreditPackage[]>(`${this.adminBaseUrl}/packages`);
  }

  getAdminTransactions(filters: CreditTransactionFilters = {}): Observable<AdminCreditTransaction[]> {
    return this.http.get<AdminCreditTransaction[]>(`${this.adminBaseUrl}/transactions`, {
      params: this.cleanParams(filters)
    });
  }

  exportAdminTransactions(filters: CreditTransactionFilters = {}): Observable<{ fileName: string; contentType: string; fileBase64: string; count: number }> {
    return this.http.get<{ fileName: string; contentType: string; fileBase64: string; count: number }>(`${this.adminBaseUrl}/transactions/export`, {
      params: this.cleanParams(filters)
    });
  }

  saveAdminPackage(pkg: Partial<CreditPackage> & { key: string; name: string; priceCents: number; baseCredits: number }): Observable<CreditPackage> {
    if (pkg.id) {
      return this.http.put<CreditPackage>(`${this.adminBaseUrl}/packages/${pkg.id}`, pkg);
    }
    return this.http.post<CreditPackage>(`${this.adminBaseUrl}/packages`, pkg);
  }

  deleteAdminPackage(id: number): Observable<{ success: boolean; id: number }> {
    return this.http.delete<{ success: boolean; id: number }>(`${this.adminBaseUrl}/packages/${id}`);
  }

  getPricingRules(): Observable<CreditPricingRule[]> {
    return this.http.get<CreditPricingRule[]>(`${this.adminBaseUrl}/pricing-rules`);
  }

  updatePricingRule(operationKey: string, payload: Partial<CreditPricingRule>): Observable<CreditPricingRule> {
    return this.http.put<CreditPricingRule>(`${this.adminBaseUrl}/pricing-rules/${encodeURIComponent(operationKey)}`, payload);
  }

  listModels(apiKey: string, provider?: string): Observable<string[]> {
    return this.http.post<string[]>(`${environment.apiUrl}/generator/listModels`, { apiKey, provider });
  }

  adminTopup(workspaceId: string, amountCredits: number, note = '', mode: 'credit' | 'debit' = 'credit'): Observable<{ transactionId: number; balanceCredits: number }> {
    return this.http.post<{ transactionId: number; balanceCredits: number }>(`${this.adminBaseUrl}/topup`, {
      workspaceId,
      amountCredits,
      note,
      mode
    });
  }

  private cleanParams(filters: CreditTransactionFilters): Record<string, string> {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>);
  }
}
